import React from 'react';
import { Button } from '@mui/material';

interface PaginationButtonsProps {
  currentPage: number;
  totalRecords: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

export const PaginationButtons: React.FC<PaginationButtonsProps> = ({
  currentPage,
  totalRecords,
  rowsPerPage,
  onPageChange,
  disabled = false
}) => {
  const totalPages = Math.ceil((totalRecords || 0) / rowsPerPage);
  const maxPagesToShow = 5;
  
  const getPageRange = () => {
    if (totalPages <= maxPagesToShow) {
      return { start: 1, end: totalPages };
    }
    
    const halfMaxPages = Math.floor(maxPagesToShow / 2);
    
    if (currentPage <= halfMaxPages) {
      return { start: 1, end: maxPagesToShow };
    }
    
    if (currentPage + halfMaxPages >= totalPages) {
      return { start: totalPages - maxPagesToShow + 1, end: totalPages };
    }
    
    return {
      start: currentPage - halfMaxPages,
      end: currentPage + halfMaxPages - (maxPagesToShow % 2 === 0 ? 1 : 0)
    };
  };

  const { start, end } = getPageRange();
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  if (totalPages <= 1) return null;

  return (
    <>
      {pages.map((pageNum) => (
        <Button
          key={pageNum}
          variant={pageNum === currentPage ? 'contained' : 'outlined'}
          onClick={() => onPageChange(pageNum)}
          size="small"
          disabled={disabled}
          sx={{
            minWidth: '32px',
            width: '32px',
            height: '32px',
            p: 0,
            borderRadius: '4px',
            color: pageNum === currentPage ? '#fff' : 'primary.main',
            backgroundColor: pageNum === currentPage ? 'primary.main' : 'transparent',
            borderColor: 'divider',
            '&:hover': {
              backgroundColor: pageNum === currentPage ? 'primary.dark' : 'action.hover',
            },
            '&:disabled': {
              color: 'text.disabled',
              borderColor: 'divider',
              backgroundColor: 'transparent',
            },
          }}
        >
          {pageNum}
        </Button>
      ))}
    </>
  );
};

export default PaginationButtons;
